import dayjs from "dayjs";
import type { ReactNode } from "react";
import {
  Avatar,
  Divider,
  Group,
  Loader,
  Stack,
  Text,
  Title,
} from "@mantine/core";
import { FormattedMessage } from "react-intl";

import SubtractIcon from "../../../assets/subtract-icon.svg?react";
import { getPensionLogo } from "../../../utils/pensionLogo";
import { REPLAY_BLOCK_CLASS } from "../../../utils/statsig";
import classes from "./ProviderList.module.css";

export interface ProviderRowData {
  id: string;
  company: string;
  logotype?: string | null;
  holderName?: string | null;
  policyNumber?: string | null;
  amount: number | null;
  dataFrom?: string | null;
  unavailable?: boolean;
}

const formatSek = (amount: number) =>
  `${Math.round(amount).toLocaleString("sv-SE")} kr`;

const formatDate = (value: string) => dayjs(value).format("YYYY/MM/DD");

const initials = (company: string) =>
  company
    .split(/\s+/)
    .slice(0, 2)
    .map((word) => word.charAt(0).toUpperCase())
    .join("");

function ProviderRow({ provider }: { provider: ProviderRowData }) {
  const { company, logotype, holderName, policyNumber, amount, dataFrom } =
    provider;

  // `logotype` is an asset filename, not a URL, so it has to be resolved to the
  // wallet artwork. A company without one falls back to the initials.
  const Logo = getPensionLogo(logotype);
  const logo = (
    <Avatar size="md" radius="xl" classNames={{ root: classes.avatar }}>
      {Logo ? <Logo width="100%" height="100%" /> : initials(company)}
    </Avatar>
  );

  if (provider.unavailable) {
    return (
      <Stack gap="xs" className={classes.row}>
        <Group gap="sm" align="flex-start" wrap="nowrap">
          {logo}
          <Stack gap={2}>
            <Group gap={4} wrap="nowrap">
              <Text fw={700} size="sm">
                {company}{" "}
                <SubtractIcon
                  aria-hidden="true"
                  style={{ verticalAlign: "-0.17em" }}
                />
              </Text>
            </Group>
            <Text c="var(--wallet-color-neutrals-gray-600)" size="xs">
              <FormattedMessage
                id="app.poaOnb.pensionCard.unavailableMessage"
                defaultMessage="Du har pension hos {company}, men just nu kan vi inte visa uppgifterna i appen."
                values={{ company }}
              />
            </Text>
          </Stack>
        </Group>
      </Stack>
    );
  }

  return (
    <Stack gap="xs" className={classes.row}>
      <Group justify="space-between" align="center" wrap="nowrap">
        <Group gap="sm" align="center" wrap="nowrap">
          {logo}
          <Stack gap={2}>
            <Text fw={700} size="sm">
              {company}
            </Text>
            {holderName && (
              <Text
                c="var(--wallet-color-neutrals-gray-600)"
                size="xs"
                className={REPLAY_BLOCK_CLASS}
              >
                {holderName}
              </Text>
            )}
            {policyNumber && (
              <Text
                c="var(--wallet-color-neutrals-gray-600)"
                size="xs"
                className={REPLAY_BLOCK_CLASS}
              >
                {policyNumber}
              </Text>
            )}
          </Stack>
        </Group>
        {amount !== null && (
          <Text
            fw={700}
            size="sm"
            style={{ whiteSpace: "nowrap" }}
            // An amount tied to a named holding is personal financial data, so it is
            // kept out of session replay like the name and the policy number are.
            className={REPLAY_BLOCK_CLASS}
          >
            {formatSek(amount)}
          </Text>
        )}
      </Group>
      {dataFrom && (
        <Stack gap="xs" className={classes.footer}>
          <Divider color="var(--wallet-color-neutrals-gray-200)" />
          <Text c="var(--wallet-color-neutrals-gray-600)" size="xs">
            <FormattedMessage
              id="app.poaOnb.pensionCard.date"
              defaultMessage="Uppgifter från {date}"
              values={{ date: formatDate(dataFrom) }}
            />
          </Text>
        </Stack>
      )}
    </Stack>
  );
}

export function ProviderList({
  providers,
  loading,
  emptyMessage,
}: {
  providers: ProviderRowData[];
  loading: boolean;
  /** Shown under the heading when nothing is coming and there is a reason why. */
  emptyMessage?: ReactNode;
}) {
  if (providers.length === 0 && !loading && !emptyMessage) return null;

  return (
    <Stack gap="xs" aria-live="polite" aria-busy={loading}>
      <Title
        order={2}
        fz="h4"
        lh="h4"
        ff="var(--wallet-font-family-sambla-sans)"
      >
        <FormattedMessage
          id="app.poaOnb.insuranceList.headline"
          defaultMessage="Dina pensioner"
        />
      </Title>
      {loading && providers.length === 0 && (
        <Text c="var(--wallet-color-neutrals-gray-600)" size="md">
          <FormattedMessage
            id="app.poaOnb.insuranceList.description"
            defaultMessage="Här kommer vi inom kort visa dina nuvarande pensionsförsäkringar"
          />
        </Text>
      )}
      {!loading && emptyMessage && (
        <div className={classes.message}>{emptyMessage}</div>
      )}
      {providers.map((provider) => (
        <ProviderRow key={provider.id} provider={provider} />
      ))}
      {loading && providers.length === 0 && (
        <Group gap="sm" wrap="nowrap">
          <Loader size="xs" color="var(--wallet-color-neutrals-gray-1000)" />
          <Text c="var(--wallet-color-neutrals-gray-600)" size="md">
            <FormattedMessage
              id="app.poaOnb.insuranceList.loadingState"
              defaultMessage="Hämtar information..."
            />
          </Text>
        </Group>
      )}
    </Stack>
  );
}
